import React, { useEffect, useState, useMemo, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { formatMedida } from "@/lib/format";
import { useLabelPrint } from "@/context/LabelPrintContext";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, Printer, CheckCircle2, Pencil, Trash2, Layers, Package, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { EntradasSection } from "@/components/EntradasSection";

const genSessionId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export default function Entrada() {
  const { hasPerm } = useAuth();
  const canIndividual = hasPerm("entrada:individual");
  const canLote = hasPerm("entrada:lote");
  const canForm = canIndividual || canLote;
  const canEditRolo = hasPerm("estoque:editar");
  const canDeleteRolo = hasPerm("estoque:excluir");
  const { printLabel, printLabels } = useLabelPrint();
  const [combinacoes, setCombinacoes] = useState([]);
  const [mode, setMode] = useState(canIndividual ? "individual" : "lote"); // individual | lote
  const [comb, setComb] = useState("");
  const [peso, setPeso] = useState("");
  const [nf, setNf] = useState("");
  const [loc, setLoc] = useState("");
  const [dataEtq, setDataEtq] = useState("");
  const [obs, setObs] = useState("");
  const [qtd, setQtd] = useState("");
  const [saving, setSaving] = useState(false);
  const [sessionRolos, setSessionRolos] = useState([]);
  const [labelSel, setLabelSel] = useState([]);
  const [entradasRefreshKey, setEntradasRefreshKey] = useState(0);
  const sessionIdRef = useRef(genSessionId());
  const [entradaCodigoAtual, setEntradaCodigoAtual] = useState(null);

  useEffect(() => {
    const ids = sessionRolos.map((r) => r.id);
    setLabelSel((prev) => [...new Set([...prev.filter((id) => ids.includes(id)), ...ids])]);
  }, [sessionRolos]);

  const toggleLabel = (id) =>
    setLabelSel((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allLabelsChecked = sessionRolos.length > 0 && labelSel.length === sessionRolos.length;
  const toggleAllLabels = () =>
    setLabelSel(allLabelsChecked ? [] : sessionRolos.map((r) => r.id));
  const printSelectedLabels = () => {
    const arr = sessionRolos.filter((r) => labelSel.includes(r.id));
    if (arr.length === 0) {
      toast.error("Marque ao menos uma etiqueta");
      return;
    }
    printLabels(arr);
  };

  const [editing, setEditing] = useState(null);
  const [edPeso, setEdPeso] = useState("");
  const [edMotivo, setEdMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/combinacoes").then((r) => setCombinacoes(r.data)).catch((e) => {const _m=apiError(e); if(_m) toast.error(_m);});
  }, []);

  const combinacoesOrdenadas = useMemo(
    () =>
      [...combinacoes].sort((a, b) =>
        `${a.tipo} ${a.cor} ${a.fornecedor_nome}`.localeCompare(`${b.tipo} ${b.cor} ${b.fornecedor_nome}`, "pt-BR")
      ),
    [combinacoes]
  );

  const combSel = combinacoes.find((c) => c.id === comb);
  const unidade = combSel?.unidade === "m" ? "m" : "kg";

  const submit = async (e) => {
    e.preventDefault();
    if (!comb) return toast.error("Selecione o tecido");
    const p = parseFloat(String(peso).replace(",", "."));
    if (!p || p <= 0) return toast.error("Informe um peso/metragem válido");

    setSaving(true);
    try {
      if (mode === "lote") {
        const n = parseInt(qtd, 10);
        if (!n || n < 1) {
          setSaving(false);
          return toast.error("Informe a quantidade de rolos");
        }
        const res = await api.post("/rolos/entrada-lote", {
          combinacao_id: comb,
          quantidade: n,
          peso_kg: p,
          nota_fiscal: nf,
          localizacao: loc,
          data_entrada_manual: dataEtq,
          observacao: obs,
          session_id: sessionIdRef.current,
        });
        setSessionRolos((prev) => [...res.data.rolos.slice().reverse(), ...prev]);
        setEntradasRefreshKey((k) => k + 1);
        if (res.data.entrada_codigo) setEntradaCodigoAtual(res.data.entrada_codigo);
        toast.success(`${res.data.total} rolos cadastrados em lote`);
        setQtd("");
        setPeso("");
      } else {
        const res = await api.post("/rolos/entrada", {
          combinacao_id: comb,
          peso_kg: p,
          nota_fiscal: nf,
          localizacao: loc,
          data_entrada_manual: dataEtq,
          observacao: obs,
          session_id: sessionIdRef.current,
        });
        setSessionRolos((prev) => [res.data, ...prev]);
        setEntradasRefreshKey((k) => k + 1);
        if (res.data.entrada_codigo) setEntradaCodigoAtual(res.data.entrada_codigo);
        toast.success(`${res.data.codigo} cadastrado — ${formatMedida(res.data.peso_atual_kg, res.data.unidade)}`);
        setPeso("");
      }
      // mantém tecido/NF/local para lançamentos rápidos em sequência; NÃO imprime automaticamente
    } catch (err) {
      {const _m=apiError(err); if(_m) toast.error(_m);};
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (rolo) => {
    setEditing(rolo);
    setEdPeso(String(rolo.peso_atual_kg).replace(".", ","));
    setEdMotivo("");
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const p = parseFloat(String(edPeso).replace(",", "."));
      const res = await api.put(`/rolos/${editing.id}`, { peso_kg: p, motivo: edMotivo });
      setSessionRolos((prev) => prev.map((r) => (r.id === editing.id ? res.data : r)));
      toast.success(`${editing.codigo} corrigido`);
      setEditing(null);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBusy(false);
    }
  };

  const excluir = async (rolo) => {
    setBusy(true);
    try {
      await api.delete(`/rolos/${rolo.id}`, { params: { motivo: "Excluído na entrada" } });
      setSessionRolos((prev) => prev.filter((r) => r.id !== rolo.id));
      toast.success(`${rolo.codigo} excluído`);
    } catch (e) {
      {const _m=apiError(e); if(_m) toast.error(_m);};
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "h-12 w-full rounded-md border-2 border-slate-300 px-3 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200";

  return (
    <div data-testid="entrada-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">Entrada de Rolo</h1>
        <p className="mt-1 text-sm text-slate-500">
          Cadastre os rolos primeiro e imprima as etiquetas em lote no final. Cada rolo é individual e rastreável.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {canForm && (
        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          {/* alternador de modo */}
          {canIndividual && canLote && (
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setMode("individual")}
              data-testid="mode-individual"
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
                mode === "individual" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              <Package size={16} /> Individual
            </button>
            <button
              onClick={() => setMode("lote")}
              data-testid="mode-lote"
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
                mode === "lote" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
              }`}
            >
              <Layers size={16} /> Em lote (rolos iguais)
            </button>
          </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="entrada-form">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Tecido</label>
              <select className={inputCls} value={comb} onChange={(e) => setComb(e.target.value)} data-testid="entrada-combinacao" required>
                <option value="">Selecione o tecido</option>
                {combinacoesOrdenadas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.tipo} {c.cor} — {c.fornecedor_nome}
                  </option>
                ))}
              </select>
            </div>

            {mode === "lote" && (
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Quantidade de rolos</label>
                <input
                  type="number"
                  min="1"
                  className={inputCls}
                  placeholder="ex: 20"
                  value={qtd}
                  onChange={(e) => setQtd(e.target.value)}
                  data-testid="entrada-qtd"
                />
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                {mode === "lote"
                  ? unidade === "m"
                    ? "Metragem de CADA rolo (m)"
                    : "Peso de CADA rolo (kg)"
                  : unidade === "m"
                  ? "Metragem do rolo (m)"
                  : "Peso real do rolo (kg)"}
              </label>
              <input
                type="text"
                inputMode="decimal"
                className={inputCls}
                placeholder={unidade === "m" ? "ex: 150,00" : "ex: 20,50"}
                value={peso}
                onChange={(e) => setPeso(e.target.value)}
                data-testid="entrada-peso"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Localização (opcional)</label>
              <input className={inputCls} placeholder="ex: 2-E (2º andar, esquerda)" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="entrada-localizacao" />
            </div>

            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Nota Fiscal (opcional)</label>
              <input className={inputCls} placeholder="Número da NF" value={nf} onChange={(e) => setNf(e.target.value)} data-testid="entrada-nf" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Data de entrada (opcional)</label>
                <input type="date" className={inputCls} value={dataEtq} onChange={(e) => setDataEtq(e.target.value)} data-testid="entrada-data-etiqueta" />
                <p className="mt-1 text-xs text-slate-400">Se preenchida, sai impressa na etiqueta.</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Observação (opcional)</label>
                <input className={inputCls} placeholder="ex: rolo com ponta emendada" value={obs} onChange={(e) => setObs(e.target.value)} data-testid="entrada-observacao" />
                <p className="mt-1 text-xs text-slate-400">Se preenchida, sai impressa na etiqueta.</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              data-testid="entrada-submit"
              className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-slate-900 text-base font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
              {mode === "lote" ? "Cadastrar lote de rolos" : "Cadastrar rolo"}
            </button>
            <p className="text-center text-xs text-slate-400">
              A etiqueta não é impressa automaticamente — cadastre tudo e use "Imprimir todas" ao lado.
            </p>
          </form>
        </section>
        )}

        <section className="rounded-md border-2 border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                <CheckCircle2 size={20} className="text-green-600" /> Cadastrados nesta sessão
              </h2>
              {entradaCodigoAtual && (
                <div className="mt-1 text-xs text-slate-500" data-testid="entrada-doc-atual">
                  Documento em aberto:{" "}
                  <span className="font-mono font-bold text-green-700">
                    {entradaCodigoAtual}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {sessionRolos.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    sessionIdRef.current = genSessionId();
                    setSessionRolos([]);
                    setLabelSel([]);
                    setEntradaCodigoAtual(null);
                    toast.success("Nova sessão iniciada — próximos rolos irão para um novo documento");
                  }}
                  data-testid="entrada-nova-sessao"
                  className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  <RotateCcw size={14} /> Nova entrada
                </button>
              )}
              <span className="rounded-md bg-slate-900 px-3 py-1 text-sm font-bold text-white" data-testid="entrada-counter">
                {sessionRolos.length} rolo{sessionRolos.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>

          {sessionRolos.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={allLabelsChecked}
                    onChange={toggleAllLabels}
                    data-testid="label-select-all"
                    className="h-4 w-4 accent-slate-900"
                  />
                  Marcar todas ({labelSel.length}/{sessionRolos.length})
                </label>
                <button
                  onClick={printSelectedLabels}
                  data-testid="print-selected-session"
                  className="flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-bold text-white hover:bg-slate-800"
                >
                  <Printer size={16} /> Imprimir selecionadas ({labelSel.length})
                </button>
              </div>
              <button
                onClick={() => printLabels(sessionRolos)}
                data-testid="print-all-session"
                className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-50"
              >
                <Printer size={18} /> Imprimir todas ({sessionRolos.length})
              </button>
            </div>
          )}

          <ul className="mt-4 divide-y divide-slate-100" data-testid="entrada-session-list">
            {sessionRolos.length === 0 && (
              <li className="py-6 text-center text-sm text-slate-400">Nenhum rolo lançado ainda nesta sessão.</li>
            )}
            {sessionRolos.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-3" data-testid={`session-rolo-${r.codigo}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={labelSel.includes(r.id)}
                    onChange={() => toggleLabel(r.id)}
                    data-testid={`label-check-${r.codigo}`}
                    className="h-4 w-4 accent-slate-900"
                  />
                  <div>
                    <span className="font-mono font-semibold text-slate-900">{r.codigo}</span>
                    <div className="text-sm text-slate-500">
                      {r.tipo} {r.cor} · {r.fornecedor_nome} · <span className="font-mono font-bold text-slate-700">{formatMedida(r.peso_atual_kg, r.unidade)}</span>
                      {r.localizacao ? <span className="text-slate-400"> · Local: {r.localizacao}</span> : ""}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => printLabel(r)} data-testid={`print-${r.codigo}`} className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                    <Printer size={16} />
                  </button>
                  {canEditRolo && (
                    <button onClick={() => openEdit(r)} data-testid={`session-edit-${r.codigo}`} className="flex items-center gap-1 rounded-md border-2 border-slate-200 px-2.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                      <Pencil size={16} />
                    </button>
                  )}
                  {canDeleteRolo && (
                    <button onClick={() => excluir(r)} disabled={busy} data-testid={`session-delete-${r.codigo}`} className="flex items-center gap-1 rounded-md border-2 border-red-200 px-2.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <EntradasSection refreshKey={entradasRefreshKey} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="session-edit-dialog">
          <DialogHeader>
            <DialogTitle>Corrigir {editing?.codigo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">
                {editing?.unidade === "m" ? "Metragem (m)" : "Peso (kg)"}
              </label>
              <input className={inputCls} value={edPeso} onChange={(e) => setEdPeso(e.target.value)} inputMode="decimal" data-testid="session-edit-peso" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Motivo (opcional)</label>
              <input className={inputCls} value={edMotivo} onChange={(e) => setEdMotivo(e.target.value)} placeholder="ex: peso digitado errado" data-testid="session-edit-motivo" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={saveEdit} disabled={busy} data-testid="session-edit-save" className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-slate-900 font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
              {busy && <Loader2 className="animate-spin" size={18} />} Salvar correção
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
